/**
 * Opening a bank or card account, and connecting its feed (piece 24, Banking
 * and Reconciliation; decision 0432).
 *
 * 🔒 A feed connection is two facts on the account: which `BankFeedProvider`
 * the connection belongs to, and that provider's own reference for it. An
 * account carries both or neither.
 *
 * 🔨 No bank credential is stored and the connect form asks for none. What is
 * held is the opaque handle the provider issued; a username, a password or a
 * bank's security answer has no column here and never gains one.
 *
 * 🔨 A pull reads the provider off the account it is pulling, never off the
 * request. A caller naming a provider is a caller who can point an account at
 * the wrong feed, and the nightly job of decision 0433 has no caller at all.
 */

import { fill, t } from '../strings';

/** The three kinds an account is opened as; a card is a second account type in the same list, never a separate section. */
export const BANK_ACCOUNT_KINDS = ['Checking', 'Savings', 'CreditCard'] as const;
export type BankAccountKindValue = (typeof BANK_ACCOUNT_KINDS)[number];

/** The label a kind reads as; the member is what is stored, and no screen draws the run-together spelling (T3214). */
export function bankKindLabel(kind: string): string {
  if (kind === 'CreditCard') return t('banking.kind.creditCard');
  if (kind === 'Savings') return t('banking.kind.savings');
  return t('banking.kind.checking');
}

export function isBankAccountKind(value: unknown): value is BankAccountKindValue {
  return typeof value === 'string' && (BANK_ACCOUNT_KINDS as readonly string[]).includes(value);
}

/** What a New bank account modal fills in: five inputs and no table, inside decision 0409's rule of thumb. */
export interface NewBankAccountDraft {
  name?: string | null;
  kind?: string | null;
  glAccountId?: string | null;
  institution?: string | null;
  accountNumberLast4?: string | null;
}

/**
 * 🔨 A new account is refused by name rather than by a field turning red with
 * nothing said: the sentence is what a person reads and what a suite asserts.
 * The account is written at Not connected, because connecting is its own act.
 */
export function newBankAccountProblem(draft: NewBankAccountDraft): string | null {
  if (!draft.name || !draft.name.trim()) return 'a bank account carries a name; type the name this account is known by';
  if (!isBankAccountKind(draft.kind)) return 'a bank account is Checking, Savings or CreditCard';
  if (!draft.glAccountId || !draft.glAccountId.trim()) return 'a bank account links to the ledger account it rolls up to';
  const last4 = (draft.accountNumberLast4 ?? '').trim();
  if (last4 && !/^\d{4}$/.test(last4)) return 'the last four digits are four digits, or are left empty';
  return null;
}

/** What Connect writes: the provider the registry has bound, and the reference that provider issued. */
export interface FeedConnectionDraft {
  providerKey?: string | null;
  connectionRef?: string | null;
}

/**
 * 🔨 Connecting names a provider the registry has bound, and one it has not is
 * refused by name — so a typo is caught where it is made rather than at the
 * first pull; the caller that draws provider names hands in the namer, so the sentence reads the names the list beside it shows. 🔨 Both facts are required together: an account carries a
 * provider and a reference, or neither.
 */
export function feedConnectionProblem(draft: FeedConnectionDraft, boundProviderKeys: readonly string[], nameOf: (providerKey: string) => string = (key) => key): string | null {
  const providerKey = (draft.providerKey ?? '').trim();
  const connectionRef = (draft.connectionRef ?? '').trim();
  if (!providerKey) return t('banking.connect.problem.noProvider');
  if (!connectionRef) return t('banking.connect.problem.noReference');
  if (!boundProviderKeys.includes(providerKey)) {
    // 🔨 Function replacers: a key carrying `$&` or `$1` is text here, never a replacement pattern.
    return fill(t('banking.connect.problem.unboundProvider'), { provider: providerKey, bound: boundProviderKeys.length ? boundProviderKeys.map(nameOf).join(', ') : t('banking.connect.problem.noneBound') });
  }
  return null;
}

/** The sentence the connect form carries while the only bound provider is the scripted fake, so nobody reads it as a real bank link. */
export function fakeProviderNotice(boundProviderKeys: readonly string[]): string | null {
  const real = boundProviderKeys.filter((key) => key !== 'scripted');
  if (real.length > 0) return null;
  return 'No real bank aggregator is connected in this environment. The only provider bound is a scripted fake, which delivers the lines a test scripted for it and nothing from any bank.';
}

/** One account as the nightly walk reports it; a failure names the account rather than counting it as done. */
export interface FeedPullOutcome {
  bankAccountId: string;
  name: string;
  delivered: number;
  created: number;
  failure: string | null;
}

/**
 * 🔨 A run reports accounts due and accounts done, so a run where three of
 * twelve failed reads as nine done rather than as success (decision 0433). An
 * account with no connection was never due.
 */
export function pullRunTotals(outcomes: readonly FeedPullOutcome[]): { due: number; done: number; created: number; failed: number } {
  const failed = outcomes.filter((row) => row.failure !== null).length;
  return {
    due: outcomes.length,
    done: outcomes.length - failed,
    created: outcomes.reduce((sum, row) => sum + row.created, 0),
    failed,
  };
}
